import { currencySymbol } from '@rc/engine';
import type { CurrencyCode } from '@rc/engine/types';

export function fmtFte(n: number, digits = 1): string {
  if (!isFinite(n)) return '—';
  return n.toFixed(digits);
}

export function fmtInt(n: number): string {
  if (!isFinite(n)) return '—';
  return Math.round(n).toLocaleString();
}

export function fmtMoney(n: number, currency: CurrencyCode = 'USD'): string {
  const sym = currencySymbol(currency);
  if (!isFinite(n) || n === 0) return `${sym}0`;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}${sym}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}${sym}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${sym}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${sym}${abs.toFixed(0)}`;
}

/** Full-precision money, for tooltips and inputs where rounding would mislead. */
export function fmtMoneyExact(n: number, currency: CurrencyCode = 'USD'): string {
  const sym = currencySymbol(currency);
  if (!isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}${sym}${Math.round(Math.abs(n)).toLocaleString('en-US')}`;
}

export function fmtPct(n: number, digits = 0): string {
  if (!isFinite(n)) return '—';
  return `${(n * 100).toFixed(digits)}%`;
}

export function fmtDelta(n: number, digits = 1): string {
  if (!isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}`;
}

export function fmtHours(n: number): string {
  if (!Number.isFinite(n) || n < 0.5) return '·';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000).toLocaleString()}K`;
  return Math.round(n).toLocaleString();
}

export function fmtRelative(input: string | number | null | undefined): string {
  if (!input) return '—';
  const ms = typeof input === 'string' ? new Date(input).getTime() : input;
  if (!ms || Number.isNaN(ms)) return '—';
  const diff = Date.now() - ms;
  const day = 24 * 60 * 60 * 1000;
  if (diff < 60 * 1000) return 'just now';
  if (diff < 60 * 60 * 1000) {
    const mins = Math.max(1, Math.round(diff / 60_000));
    return `${mins}m ago`;
  }
  if (diff < day) return `${Math.round(diff / (60 * 60 * 1000))}h ago`;
  if (diff < 7 * day) return `${Math.round(diff / day)}d ago`;
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || `id-${Date.now()}`;
}
